import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ShieldCheck, Database, Lock, Server, KeyRound, FileCheck, Users, Globe } from 'lucide-react';

const sections = [
  {
    icon: ShieldCheck,
    title: 'Datensicherheit',
    content: `Der Schutz Ihrer Daten hat für uns höchste Priorität. Alle Verbindungen zu unserem Dienst sind durch TLS/SSL-Verschlüsselung (HTTPS) geschützt. Das bedeutet, dass sämtliche Daten, die zwischen Ihrem Browser und unseren Servern übertragen werden, verschlüsselt sind und von Dritten nicht eingesehen werden können.`,
  },
  {
    icon: Lock,
    title: 'Passwortsicherheit',
    content: `Passwörter werden niemals im Klartext gespeichert. Wir verwenden den Industriestandard bcrypt, einen besonders sicheren Hash-Algorithmus mit automatischer Salt-Generierung. Selbst im unwahrscheinlichen Fall eines Datenbankzugriffs durch Unbefugte können gespeicherte Passwörter nicht in Klartext zurückgewandelt werden.`,
  },
  {
    icon: KeyRound,
    title: 'Authentifizierung',
    content: `Nach der Anmeldung wird ein signierter JSON Web Token (JWT) erstellt, der für die Dauer Ihrer Sitzung gültig ist. Dieser Token enthält keine sensiblen Daten und wird bei jeder Anfrage überprüft. Unbefugte Zugriffe werden damit zuverlässig verhindert.`,
  },
  {
    icon: Database,
    title: 'Datenspeicherung',
    content: `Ihre Daten werden in einer MongoDB-Datenbank gespeichert, die nur über interne Netzwerkverbindungen erreichbar ist und nicht öffentlich zugänglich ist. Personenbezogene Daten wie Name, E-Mail-Adresse und Firmeninformationen werden getrennt von den Analysedaten verwaltet.`,
  },
  {
    icon: Server,
    title: 'Serverstandort & Hosting',
    content: `Unsere Server befinden sich in Deutschland bzw. der Europäischen Union und unterliegen damit den strengen Datenschutzbestimmungen der DSGVO. Es findet keine Datenübertragung in Drittländer statt.`,
  },
  {
    icon: FileCheck,
    title: 'Eingabevalidierung & XSS-Schutz',
    content: `Alle Benutzereingaben werden serverseitig validiert und mittels Sanitisierung (bleach) bereinigt. Damit werden Cross-Site-Scripting (XSS) Angriffe und das Einschleusen von Schadcode zuverlässig verhindert.`,
  },
  {
    icon: Users,
    title: 'Zugriffsrechte & Rollensystem',
    content: `Das System unterscheidet zwischen Administratoren und normalen Benutzern. Jeder Nutzer sieht ausschließlich seine eigenen Analysen. Administratoren können zusätzlich Systemeinstellungen verwalten und alle Analysen einsehen. Konfigurationsänderungen sind ausschließlich Administratoren vorbehalten.`,
  },
  {
    icon: Globe,
    title: 'DSGVO-Rechte',
    content: `Gemäß der Datenschutz-Grundverordnung (DSGVO) haben Sie jederzeit das Recht auf Auskunft, Berichtigung und Löschung Ihrer Daten. In Ihrem Nutzerprofil können Sie alle gespeicherten Daten als JSON-Datei exportieren oder Ihr Konto inklusive aller zugehörigen Analysedaten unwiderruflich löschen.`,
  },
];

export default function InfoPage() {
  return (
    <div className="max-w-3xl mx-auto py-12 px-6 animate-fade-in" data-testid="info-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <ShieldCheck className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sicherheit & Datenschutz</h1>
          <p className="text-sm text-muted-foreground">Wie wir Ihre Daten schützen</p>
        </div>
      </div>

      <p className="text-muted-foreground text-sm mb-8 ml-[52px]">
        Transparenz ist uns wichtig. Auf dieser Seite erfahren Sie, welche technischen und organisatorischen Maßnahmen wir zum Schutz Ihrer Daten einsetzen.
      </p>

      <div className="space-y-4">
        {sections.map((s, i) => {
          const Icon = s.icon;
          return (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="flex gap-4">
                  <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="h-4.5 w-4.5 text-muted-foreground" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-sm mb-1.5" data-testid={`info-section-${i}`}>{s.title}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">{s.content}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Separator className="my-8" />

      <div className="text-center space-y-3">
        <p className="text-xs text-muted-foreground">
          Bei Fragen zur Datensicherheit wenden Sie sich bitte an den Betreiber dieses Tools.
        </p>
        <div className="flex justify-center gap-3">
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2" data-testid="info-back-btn">
              <ArrowLeft className="h-3.5 w-3.5" /> Zur Startseite
            </Button>
          </Link>
          <Link to="/datenschutz">
            <Button variant="ghost" size="sm" data-testid="info-datenschutz-link">Datenschutzerklärung</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
